import { useEffect, useState } from "react";
import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  CircularProgress,
  Typography,
} from "@mui/material";
import api from "../../../api";

/**
 * Flatten a recursive category tree into a flat array with level info.
 * Handles both `children` and `all_children` field names.
 */
export const flattenCategoryTree = (cats, level = 0) => {
  let result = [];
  for (const cat of cats) {
    result.push({ ...cat, level });
    const children = cat.children || cat.all_children || [];
    if (children.length > 0) {
      result = result.concat(flattenCategoryTree(children, level + 1));
    }
  }
  return result;
};

/**
 * Reusable category tree dropdown styled like the examplify screenshot:
 * - Root level: "• Name" bold, light blue background
 * - Children: "└ Name" indented, with left blue border lines
 *
 * Props:
 *  value              — selected category id (number | "")
 *  onChange           — (id) => void
 *  label              — label string (default "Category")
 *  size               — MUI size ("small" | "medium")
 *  fullWidth          — boolean
 *  noneLabel          — label for empty option (default "None")
 *  showNone           — whether to show the None option (default true)
 *  disabled           — boolean
 *  sx                 — additional sx for FormControl
 *  externalCategories — if provided, skip fetching (pass already-fetched tree)
 *  error              — boolean, show error state
 *  helperText         — string, show below the select
 */
const CategoryTreeSelect = ({
  value = "",
  onChange,
  label = "Category",
  size = "small",
  fullWidth = true,
  noneLabel = "None",
  showNone = true,
  disabled = false,
  sx = {},
  externalCategories = null,
  error = false,
  helperText = "",
}) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (externalCategories !== null) {
      setCategories(externalCategories);
      return;
    }
    setLoading(true);
    api
      .get("/admin/mock-exam/category-tree")
      .then((res) => setCategories(res.data?.data || []))
      .catch(() => setCategories([]))
      .finally(() => setLoading(false));
  }, [externalCategories]);

  const flatCategories = flattenCategoryTree(categories);

  return (
    <FormControl size={size} fullWidth={fullWidth} disabled={disabled || loading} error={error} sx={sx}>
      <InputLabel>{loading ? "Loading…" : label}</InputLabel>
      <Select
        value={value}
        label={loading ? "Loading…" : label}
        onChange={(e) => onChange(e.target.value)}
        startAdornment={loading ? <CircularProgress size={14} sx={{ mr: 1 }} /> : undefined}
        renderValue={(selected) => {
          if (!selected) return <em style={{ color: "#aaa" }}>{noneLabel}</em>;
          const found = flatCategories.find((c) => c.id === selected);
          return found ? found.name : selected;
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              borderRadius: 2,
              boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
              mt: 0.5,
            },
          },
        }}
      >
        {showNone && (
          <MenuItem value="">
            <em style={{ color: "#aaa" }}>{noneLabel}</em>
          </MenuItem>
        )}
        {flatCategories.map((cat) => (
          <MenuItem
            key={cat.id}
            value={cat.id}
            sx={{
              px: 2,
              py: 0.75,
              ...(cat.level === 0 && {
                bgcolor: "rgba(25, 118, 210, 0.06)",
                "&:hover": { bgcolor: "rgba(25, 118, 210, 0.12)" },
                "&.Mui-selected": { bgcolor: "rgba(25, 118, 210, 0.14)" },
                "&.Mui-selected:hover": { bgcolor: "rgba(25, 118, 210, 0.18)" },
              }),
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
              {/* Indent guides for child levels */}
              {cat.level > 0 &&
                Array.from({ length: cat.level }).map((_, i) => (
                  <Box
                    key={i}
                    sx={{
                      width: 2,
                      alignSelf: "stretch",
                      bgcolor: "primary.main",
                      opacity: 0.3,
                      mr: 1.5,
                      flexShrink: 0,
                      borderRadius: 1,
                    }}
                  />
                ))}

              {cat.level === 0 ? (
                /* Root level: bullet + bold */
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    component="span"
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      bgcolor: "primary.main",
                      flexShrink: 0,
                      display: "inline-block",
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, color: "primary.main", fontFamily: "monospace" }}
                  >
                    {cat.name}
                  </Typography>
                </Box>
              ) : (
                /* Child levels: └ prefix + name */
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Typography
                    component="span"
                    sx={{
                      fontSize: "0.8rem",
                      color: "primary.main",
                      opacity: 0.7,
                      fontFamily: "monospace",
                      lineHeight: 1,
                      flexShrink: 0,
                    }}
                  >
                    └
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "primary.main", fontFamily: "monospace" }}
                  >
                    {cat.name}
                  </Typography>
                </Box>
              )}
            </Box>
          </MenuItem>
        ))}
      </Select>
      {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
    </FormControl>
  );
};

export default CategoryTreeSelect;
